import { Injectable } from '@angular/core';
import { SqliteDbService } from './sqlite-db.service';
import {
  HttpBackend,
  HttpClient,
  HttpErrorResponse,
} from '@angular/common/http';
import { Filesystem, Directory } from '@capacitor/filesystem';
import { BehaviorSubject, firstValueFrom, lastValueFrom } from 'rxjs';

import { DeviceInfoService } from './device-info.service';
import { App } from '@capacitor/app';
import { environment } from '../../../environments/environment';
import { NativeUploadUpdateStatus } from '../../../global.variable';

@Injectable({
  providedIn: 'root',
})
export class NativeBgUploadService {
  apiResponseSubject = new BehaviorSubject({});

  private nativeAppUploads = new Map<string, object>();
  isNativeUploadRuninng = false;
  offlineMode: boolean = !navigator.onLine;

  started = false;
  constructor(
    private sqliteDb: SqliteDbService,
    private http: HttpClient,
    private deviceInfoService: DeviceInfoService,
    httpBackend: HttpBackend,
  ) {
    // This will skip auth interceptor. Need to learn which apis can be moved. This would have been done because earlier we had lot of codes that didn't have auth tokens
    this.http = new HttpClient(httpBackend);
    // One listener for the app's lifetime. Native uploads report back here.
    // Uploader.addListener('events', (event: any) =>
    //   this.onUploadEventFromNativeApp(event),
    // );
  }
  async start(): Promise<void> {
    if (this.started) return;
    this.started = true;

    // Cold start only: rows left "in progress" mean the app died mid-send.
    // Don't call this on resume, because uploads may legitimately still be running then.
    await this.recoverStuck();
    await this.syncIfOnline();

    // App comes back to the foreground
    App.addListener('appStateChange', ({ isActive }) => {
      if (isActive) this.syncIfOnline();
    });
    this.deviceInfoService.deviceInfoSubject.subscribe((value: any) => {
      if ('appStatus' in value) {
        this.offlineMode = !value?.appStatus;
        this.syncIfOnline();
      }
    });
  }

  /** Also call this right after saving a row with addJobUpdate(). */
  async syncIfOnline(): Promise<void> {
    if (!this.offlineMode) this.syncPendingNativeUpdates();
  }
  private async onUploadEventFromNativeApp(event: any): Promise<void> {
    const statusCode = event.payload.statusCode;
    const responseBody = event.payload.responseBody;
    const errorMessage = event.payload?.headers?.['X-Upload-Error-Message'];

    const uploadId = event?.payload?.id;
    if (!uploadId || (event.name !== 'completed' && event.name !== 'failed'))
      return;

    let row: any = this.nativeAppUploads.get(uploadId);

    if (row?.id === undefined) {
      // A very fast upload can finish before startUpload's id is stored. Try once more.
      await new Promise((r) => setTimeout(r, 1000));
      row = this.nativeAppUploads.get(uploadId);
    }
    if (row?.id === undefined) return; // not ours, or from a previous launch (recoverStuck handles it)

    this.nativeAppUploads.delete(uploadId);

    if (event.name === 'completed') {
      const code = Number(event.payload.statusCode);
      if (!code || (code >= 200 && code < 300)) {
        await this.markDone(row?.id, row?.type);
      } else {
        await this.setFailed(row?.id, `HTTP ${code}`);
      }
    } else {
      await this.setFailed(
        row?.id,
        String(event.payload.error ?? 'upload failed'),
      );
    }
  }
  async syncPendingNativeUpdates(): Promise<void> {
    if (this.isNativeUploadRuninng) return;
    this.isNativeUploadRuninng = true;
    const userData = JSON.parse(localStorage.getItem('userData') || '{}');
    const auth_token = userData?.access;
    const apiResponse: any =
      await this.deviceInfoService.getApiHeadersAndExtras(auth_token);

    const headerDict = apiResponse?.headerDict;
    try {
      const rows: any =
        (await this.sqliteDb.getAllSavedUpdatesByStatuses([
          NativeUploadUpdateStatus.PENDING,
          NativeUploadUpdateStatus.FAILED,
        ])) ?? [];
      for (const row of rows) {
        await this.sendRow(row, headerDict);
      }
      setTimeout(() => {
        this.deviceInfoService.deviceInfoSubject.next({
          syncComplete: true,
        });
      }, 1000);
    } finally {
      this.isNativeUploadRuninng = false;
    }
  }
  private async sendRow(row: any, headerDict: any): Promise<void> {
    await this.sqliteDb.execute(
      `UPDATE saved_updates SET status = ?, updatedAt = ? WHERE id = ?`,
      [NativeUploadUpdateStatus.IN_PROGRESS, Date.now(), row.id],
    );

    try {
      const requestData = JSON.parse(row?.data);

      // if (requestData?.uploadedFilePath) {
      //   await this.sendWithFile(row, requestData, headerDict);
      // } else {
      //    await this.sendWithoutFile(row, requestData, headerDict);

      // }

      await this.sendToServer(row, requestData, headerDict);
    } catch (err) {
      await this.handleFailure(row.id, err);
    }
  }
  getServerUrlPath(row: any) {
    let updatedPath: string = '';

    switch (row?.type) {
      case 'job':
        updatedPath = 'api/job/update_job/';
        break;
      case 'scan_checkpoint':
        updatedPath = 'api/patrol/scan_checkpoint/';
        break;
      case 'patrol':
        updatedPath = 'api/patrol/add_update/';
        break;
      case 'register_qr':
        updatedPath = 'api/check_point/register_qr/';
        break;
      case 'runsheet':
        updatedPath = 'api/run_sheet/add_update/';
        break;
    }
    if (environment.nativeAppUrl) {
      return `https://${environment.nativeAppUrl}/${updatedPath}`;
    }
    return updatedPath;
  }
  // private async sendWithFile(row: any, data: any, headerDict: any) {
  //   const { uri } = await Filesystem.getUri({
  //     path: data?.uploadedFilePath,
  //     directory: Directory.Data,
  //   });

  //   console.log(
  //     this.getServerUrlPath(row, true),
  //     'this.getServerUrlPath(row, true)',
  //   );

  //   const { id } = await Uploader.startUpload({
  //     filePath: uri,
  //     serverUrl: this.getServerUrlPath(row, true),
  //     method: 'POST',
  //     headers: headerDict,
  //     parameters: data?.params,
  //     notificationTitle: 'Uploading update',
  //     maxRetries: 3,
  //     uploadType: 'multipart',
  //     fileField: 'file',
  //   });

  //   this.nativeAppUploads.set(id, { id: row?.id, type: row?.type });
  // }
  private async sendToServer(row: any, data: any, headerDict: any) {
    const formData = new FormData();
    Object.keys(data?.body).forEach((key: any) => {
      formData.set(key, data?.body?.[key]);
    });
    const updatedAt: any = row?.createdAt || new Date().getTime();

    formData.set('indexDBId', row?.id);
    formData.set('updated_at', String(updatedAt / 1000));

    if (data?.uploadedFilePath) {
      const readFileResult = await Filesystem.readFile({
        path: data?.uploadedFilePath,
        directory: Directory.Data,
      });

      // convert to blob

      const response = await fetch(
        `data:${data?.body?.mimeType || 'image/avif'};base64,${readFileResult.data}`,
      );
      const fileBlob = await response.blob();
      formData.append('file', fileBlob);
    }

    const response: any = await lastValueFrom(
      this.http.request('POST', this.getServerUrlPath(row), {
        body: formData,
        headers: headerDict,
        params: data?.params,
      }),
    );

    if (response?.status == 'success') {
      await this.markDone(row, data?.uploadedFilePath);
    } else {
      await this.handleFailure(row.id, response);
    }
    this.apiResponseSubject.next(response);
  }
  async recoverStuck(): Promise<void> {
    await this.sqliteDb.execute(
      `UPDATE saved_updates SET status = ?, updatedAt = ? WHERE status = ?`,
      [
        NativeUploadUpdateStatus.PENDING,
        Date.now(),
        NativeUploadUpdateStatus.IN_PROGRESS,
      ],
    );
  }
  private async markDone(row: any, filePath?: string) {
    await this.sqliteDb.deleteSavedUpdateRow(row.type, row?.id);
    // Optional: delete the local file here to free space. Only do this if users
    // don't need to view synced files offline.
    if (filePath) {
      const deleteileResult = await Filesystem.deleteFile({
        path: filePath,
        directory: Directory.Data,
      });
    }
  }
  private async handleFailure(id: number, err: any) {
    if (err instanceof HttpErrorResponse) {
      await this.setFailed(
        id,
        `HTTP ${err.status}: ${JSON.stringify(err.error)}`,
      );
    } else {
      await this.setFailed(id, String((err as any)?.message ?? err));
    }
  }
  private async setFailed(id: number, reason: string) {
    const rows = await this.sqliteDb.query(
      `SELECT retryResponse FROM saved_updates WHERE id = ?`,
      [id],
    );

    let history: string[] = [];
    const existing = rows?.[0]?.retryResponse;
    if (existing) {
      try {
        history = JSON.parse(existing);
        if (!Array.isArray(history)) history = [existing];
      } catch {
        history = [existing];
      }
    }
    history.push(`${new Date().toISOString()}: ${reason}`);

    await this.sqliteDb.execute(
      `UPDATE saved_updates SET status = ?, retryResponse = ?, updatedAt = ? WHERE id = ?`,
      [
        NativeUploadUpdateStatus.FAILED,
        JSON.stringify(history),
        Date.now(),
        id,
      ],
    );
  }

  private isRetryable(status: number): boolean {
    return status === 0 || status === 408 || status === 429 || status >= 500;
  }
}
